import React, { useState } from 'react';
import { useNavigate, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, ShieldCheck } from 'lucide-react';
import { signInErrorMessage } from '../lib/signInError';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // MFA step-up state
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const { login, verifyMfa, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sessionExpired = searchParams.get('expired') === '1';
  // APR-11c — where the person was going (ProtectedRoute kept it). Only an
  // in-app path is honoured, never an external address.
  const from = location.state?.from;
  const destination = typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/dashboard';

  if (user) {
    return <Navigate to={destination} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result?.mfa_required && result?.mfa_token) {
        setMfaToken(result.mfa_token);
        setMfaCode('');
        return; // stay on this page to collect the code
      }
      navigate(destination);
    } catch (err) {
      setError(signInErrorMessage(err, 'Login failed. Please check your credentials.'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaToken, mfaCode);
      navigate(destination);
    } catch (err) {
      setError(signInErrorMessage(err, 'Invalid code. Try the next code or use a backup code.'));
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    setMfaToken(null);
    setMfaCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8">
          <div className="flex flex-col items-center mb-8">
            <img src="/northline-logo.png" alt="Northline" className="w-16 h-16 rounded-lg object-cover mb-4" />
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">NORTHLINE</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Real Estate Investment Platform</p>
          </div>

          <form onSubmit={mfaToken ? handleMfaSubmit : handleSubmit} className="space-y-5">
            {sessionExpired && !mfaToken && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3" data-testid="session-expired-notice">
                <p className="text-sm text-amber-800 dark:text-amber-200">Your session has expired. Please sign in again.</p>
              </div>
            )}

            {mfaToken ? (
              <>
                <div className="flex flex-col items-center gap-1 pb-2">
                  <ShieldCheck className="w-8 h-8 text-cyan-700 dark:text-cyan-400" />
                  <h2 className="text-base font-semibold text-slate-900 dark:text-white">Two-factor authentication</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                    Enter the 6-digit code from your authenticator app, or use a backup code.
                  </p>
                </div>
                <div>
                  <Label htmlFor="mfa-code" className="text-slate-700 dark:text-slate-300">Authentication code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    required
                    autoFocus
                    className="mt-1.5 text-center tracking-[0.3em] text-lg font-mono"
                    placeholder="123 456"
                    data-testid="mfa-code-input"
                  />
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    Lost your device? Enter one of your backup codes instead.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5"
                    placeholder="you@company.com"
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Password</Label>
                    <a href="/forgot-password"
                       className="text-xs text-cyan-700 hover:text-cyan-800 font-medium dark:text-cyan-400"
                       data-testid="login-forgot-password-link">
                      Forgot password?
                    </a>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
              data-testid={mfaToken ? "mfa-submit-button" : "login-submit-button"}
            >
              {loading ? (mfaToken ? 'Verifying…' : 'Signing in...') : (mfaToken ? 'Verify & continue' : 'Sign In')}
            </Button>

            {mfaToken && (
              <button
                type="button"
                onClick={cancelMfa}
                className="w-full text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                data-testid="mfa-cancel-link"
              >
                ← Use a different account
              </button>
            )}
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 text-center space-y-2">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              New to Northline?{' '}
              <a href="/signup" className="text-cyan-700 hover:text-cyan-800 font-medium dark:text-cyan-400" data-testid="signup-link">
                Start your 14-day free trial
              </a>
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Property manager?{' '}
              <a href="/pm/login" className="text-cyan-700 hover:text-cyan-800 font-medium dark:text-cyan-400" data-testid="pm-portal-link">
                Sign in to the manager portal
              </a>
            </p>
            {/* STATUS-1 — where to look when sign-in itself is the problem. */}
            <p className="text-xs">
              <a href="/status" className="text-slate-600 dark:text-slate-400 underline" data-testid="status-link">
                System status
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
